import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const StudentPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/student/payments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getStatusColor = (status) => {
    if (status === 'paid') return 'bg-green-100 text-green-700';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (status === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    if (status === 'paid') return '✅ Payé';
    if (status === 'pending') return '⏳ En attente';
    if (status === 'failed') return '❌ Échoué';
    if (status === 'refunded') return '↩️ Remboursé';
    return status;
  };

  const totalPaid = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const totalPending = payments
    .filter((p) => p.status === 'pending')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const total = totalPaid + totalPending;

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">💳 Mes frais scolaires</h1>
        <p className="text-gray-500">Consultez votre situation financière</p>
      </div>

      {/* Message d'information */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Pour effectuer un paiement, veuillez contacter votre parent ou tuteur.
          Le paiement en ligne sera bientôt disponible.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <p className="text-sm text-gray-500 mb-1">💰 Total payé</p>
          <p className="text-2xl font-bold text-blue-600">{formatAmount(totalPaid)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
          <p className="text-sm text-gray-500 mb-1">⏳ Reste à payer</p>
          <p className="text-2xl font-bold text-orange-600">{formatAmount(totalPending)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-gray-700">
          <p className="text-sm text-gray-500 mb-1">📊 Total</p>
          <p className="text-2xl font-bold text-gray-800">{formatAmount(total)}</p>
        </div>
      </div>

      {/* Historique */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-[#2C3E50]">
            📋 Historique des paiements ({payments.length})
          </h3>
        </div>
        {payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg mb-2">📭 Aucun paiement enregistré</p>
            <p className="text-sm">Vous n'avez aucun paiement pour le moment.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Référence</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Montant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Méthode</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-sm">{payment.reference}</td>
                    <td className="py-3 px-4 font-bold">{formatAmount(payment.amount)}</td>
                    <td className="py-3 px-4 text-sm">{payment.date || '-'}</td>
                    <td className="py-3 px-4 text-sm capitalize">{payment.method || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(payment.status)}`}>
                        {getStatusLabel(payment.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPayments;